import React, { useCallback, useState } from 'react';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Dropzone } from '../../../CommonComponents/OPL/Dropzone/Dropzone';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { Icon } from '../../../CommonComponents/OPL/Icon/Icon';
import { useCreateReconciliationBatchMutation } from '../api/useCreateReconciliationBatchMutation';

interface Props {
  clientId: number;
  onUploaded: (batchId: string, itemId: string) => void;
}

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

export const UploadPlanStep = ({ clientId, onUploaded }: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: createBatch, isLoading } = useCreateReconciliationBatchMutation();

  const handleDrop = useCallback((files: File[]) => {
    setError(null);

    const file = files[0];

    if (!file) {
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('File size exceeds the 50 MB limit.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are accepted.');
      return;
    }
    setSelectedFile(file);
  }, []);

  const handleSubmit = async () => {
    if (!selectedFile) {
      return;
    }

    setError(null);

    try {
      const result = await createBatch({ clientId, file: selectedFile });
      onUploaded(result.batchId, result.itemId);
    } catch (e) {
      const message = e instanceof Error ? e.message : '';
      setError(message || 'Could not upload the file. Please try again.');
    }
  };

  return (
    <Box>
      <Text color="neutral.600" mb="16">
        Upload a financial planning PDF (e.g. eMoney, MoneyGuidePro, RightCapital). We'll extract
        the data and match it against this household so you can review what to keep before applying.
      </Text>

      {error && <Alert variant="error" title="Upload failed" message={error} mb="16" />}

      {!selectedFile ? (
        <Dropzone
          options={{
            onDropAccepted: handleDrop,
            accept: ['.pdf', 'application/pdf'],
            multiple: false,
            disabled: isLoading,
          }}
        />
      ) : (
        <>
          <Box
            display="flex"
            alignItems="center"
            p="12"
            borderRadius="medium"
            css={({ theme }) => ({
              border: `1px solid ${theme.colors.neutral['200']}`,
              gap: theme.space[12],
            })}
          >
            <Text fontSize="h4" color="primary.500">
              <Icon className="far fa-file-pdf" />
            </Text>

            <Box>
              <Text fontWeight={500} isTruncated>
                {selectedFile.name}
              </Text>
              <Text fontSize="small" color="neutral.500">
                {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
              </Text>
            </Box>

            <Button
              variant="subtleInactive"
              ml="auto"
              onClick={() => setSelectedFile(null)}
              disabled={isLoading}
            >
              Remove
            </Button>
          </Box>

          <Box mt="16">
            <Button onClick={handleSubmit} disabled={isLoading} isLoading={isLoading}>
              Upload and continue
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
